import { GshopListingStatus } from "@redotech/redo-prisma-client/enums";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  attributeDefinitionIdFormat,
  googleShoppingListingIdFormat,
} from "../../ids";
import { AttributeType, AttributeValueSchema } from "../attributes";
import { BaseProductFamilyListingSchema, ListingType } from "../base-listing";
import { RedoListingStatus } from "../redo-listing-status";
import {
  GoogleShoppingListingMediaSchema,
  GoogleShoppingListingMediaWithGoogleIds,
  GoogleShoppingListingMediaWithoutRedoIds,
} from "./google-shopping-media";
import {
  GoogleShoppingVariantListingSchema,
  GoogleShoppingVariantListingWithGoogleIds,
  GoogleShoppingVariantListingWithoutRedoIds,
  GoogleShoppingVariantMappingSchema,
} from "./google-shopping-variant";

export const GoogleShoppingListingStatusSchema = createEnum(GshopListingStatus);

export const GoogleShoppingListingIdWithStatusSchema = z.object({
  id: googleShoppingListingIdFormat.schema,
  status: GoogleShoppingListingStatusSchema,
});
export type GoogleShoppingListingIdWithStatus = z.infer<
  typeof GoogleShoppingListingIdWithStatusSchema
>;

export function convertGoogleShoppingListingStatusToRedoListingStatus(
  status: GshopListingStatus,
): RedoListingStatus {
  switch (status) {
    case GshopListingStatus.ACTIVE:
      return RedoListingStatus.ACTIVE;
    case GshopListingStatus.DRAFT:
      return RedoListingStatus.DRAFT;
    case GshopListingStatus.DISAPPROVED:
      return RedoListingStatus.INACTIVE;
    case GshopListingStatus.PENDING:
      return RedoListingStatus.DRAFT;
    default:
      assertNever(status);
  }
}

/**
 * Google Shopping product field types for attribute mapping
 * Matches the SQL enum: gshop_product_field_type
 */
export enum GoogleShoppingProductFieldType {
  title = "title",
  description = "description",
  brand = "brand",
  google_product_category = "google_product_category",
  product_type = "product_type",
  link = "link",
  custom_label_0 = "custom_label_0",
  custom_label_1 = "custom_label_1",
  custom_label_2 = "custom_label_2",
  custom_label_3 = "custom_label_3",
  custom_label_4 = "custom_label_4",
}
export const GoogleShoppingProductFieldTypeSchema = createEnum(
  GoogleShoppingProductFieldType,
);

export const GoogleShoppingListingSchema_ =
  BaseProductFamilyListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: z.bigint(),
    googleProductId: z.string().nullable(),
    title: z.string(),
    description: z.string(),
    brand: z.string().nullable(),
    googleProductCategory: z.string().nullable(),
    productType: z.string().nullable(),
    link: z.string(),
    customLabel0: z.string().nullable(),
    customLabel1: z.string().nullable(),
    customLabel2: z.string().nullable(),
    customLabel3: z.string().nullable(),
    customLabel4: z.string().nullable(),
    feedLabel: z.string(),
    contentLanguage: z.string(),
    status: GoogleShoppingListingStatusSchema,
    variants: z.array(GoogleShoppingVariantListingSchema),
    media: z.array(GoogleShoppingListingMediaSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
  });
export const GoogleShoppingListingSchema: z.ZodType<GoogleShoppingListing> =
  GoogleShoppingListingSchema_;
export interface GoogleShoppingListing
  extends z.infer<typeof GoogleShoppingListingSchema_> {}

export type GoogleShoppingListingWithoutRedoIds = Omit<
  GoogleShoppingListing,
  "id" | "variants" | "media" | "productFamilyId"
> & {
  variants: GoogleShoppingVariantListingWithoutRedoIds[];
  media: GoogleShoppingListingMediaWithoutRedoIds[];
};

export type GoogleShoppingListingWithGoogleIds = Omit<
  GoogleShoppingListing,
  "id" | "variants" | "media"
> & {
  googleProductId: string;
  variants: GoogleShoppingVariantListingWithGoogleIds[];
  media: GoogleShoppingListingMediaWithGoogleIds[];
};

export const GoogleShoppingProductAttributeType = {
  [GoogleShoppingProductFieldType.title]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.description]: AttributeType.RichText,
  [GoogleShoppingProductFieldType.brand]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.google_product_category]:
    AttributeType.ShortText,
  [GoogleShoppingProductFieldType.product_type]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.link]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.custom_label_0]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.custom_label_1]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.custom_label_2]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.custom_label_3]: AttributeType.ShortText,
  [GoogleShoppingProductFieldType.custom_label_4]: AttributeType.ShortText,
} satisfies Record<GoogleShoppingProductFieldType, AttributeType>;

const GoogleShoppingProductMappingSchema_ = z.object({
  fieldName: GoogleShoppingProductFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const GoogleShoppingProductMappingSchema: z.ZodType<GoogleShoppingProductMapping> =
  GoogleShoppingProductMappingSchema_;
export interface GoogleShoppingProductMapping
  extends z.infer<typeof GoogleShoppingProductMappingSchema_> {}

export const GoogleShoppingMappingSchema_ = z.object({
  productMappings: z.array(GoogleShoppingProductMappingSchema).readonly(),
  variantMappings: z.array(GoogleShoppingVariantMappingSchema).readonly(),
});
export const GoogleShoppingMappingSchema: z.ZodType<GoogleShoppingMapping> =
  GoogleShoppingMappingSchema_;
export interface GoogleShoppingMapping
  extends z.infer<typeof GoogleShoppingMappingSchema_> {}

export const GoogleShoppingProductAttributeSchema = {
  [GoogleShoppingProductFieldType.title]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GoogleShoppingProductFieldType.title]
    ].nullable(),
  [GoogleShoppingProductFieldType.description]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.description
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.brand]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GoogleShoppingProductFieldType.brand]
    ].nullable(),
  [GoogleShoppingProductFieldType.google_product_category]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.google_product_category
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.product_type]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.product_type
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.link]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[GoogleShoppingProductFieldType.link]
    ].nullable(),
  [GoogleShoppingProductFieldType.custom_label_0]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.custom_label_0
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.custom_label_1]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.custom_label_1
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.custom_label_2]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.custom_label_2
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.custom_label_3]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.custom_label_3
      ]
    ].nullable(),
  [GoogleShoppingProductFieldType.custom_label_4]:
    AttributeValueSchema[
      GoogleShoppingProductAttributeType[
        GoogleShoppingProductFieldType.custom_label_4
      ]
    ].nullable(),
};

export type GoogleShoppingProductAttributeValueType = {
  [key in GoogleShoppingProductFieldType]: (typeof GoogleShoppingProductAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGoogleShoppingProductAttributeValue<
  T extends GoogleShoppingProductFieldType,
>(field: T, value: unknown): GoogleShoppingProductAttributeValueType[T] | null {
  return GoogleShoppingProductAttributeSchema[field].parse(
    value,
  ) as GoogleShoppingProductAttributeValueType[T];
}

export function getGoogleShoppingProductAttributeType<
  T extends GoogleShoppingProductFieldType,
>(field: T): (typeof GoogleShoppingProductAttributeType)[T] {
  return GoogleShoppingProductAttributeType[field];
}
